<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Académico / Horario</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>
          
          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="data && data.length > 0" class="mb-6">
            <!-- Filtro de días -->
            <div class="mb-4">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-lg font-semibold mb-2 min-w-full">Día:</span>
                <button 
                  @click="filtrarPorDia('todos')"
                  :class="['px-2 py-1 rounded text-sm', filtroSemana === 'todos' ? 'bg-gray-600 text-white' : 'bg-gray-200']"
                >
                  Todos
                </button>
                <button 
                  v-for="dia in diasSemana" 
                  :key="dia.numero"
                  @click="filtrarPorDia(dia.numero)"
                  :class="[
                    'px-2 py-1 rounded text-sm', 
                    filtroSemana === dia.numero ? 'bg-gray-600 text-white' : 'bg-gray-200',
                    !diasConClases.includes(dia.numero) ? 'opacity-50 cursor-not-allowed' : ''
                  ]"
                  :disabled="!diasConClases.includes(dia.numero)"
                >
                  {{ dia.nombre }}
                </button>
              </div>
            </div>

            <!-- Botones de Visualizar y Descargar Horario PDF -->
            <div class="flex flex-col sm:flex-row gap-2 mb-4">
              <button
                @click="viewScheduleDocument"
                :disabled="loadingScheduleDoc"
                class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                <span v-if="loadingScheduleDoc">Cargando...</span>
                <span v-else>Visualizar horario</span>
              </button>
              <button
                @click="downloadScheduleDocument"
                :disabled="loadingScheduleDoc"
                class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                <span v-if="loadingScheduleDoc">Cargando...</span>
                <span v-else>Descargar horario</span>
              </button>
            </div>

            <!-- Mensajes de estado para descarga o visualización -->
            <div v-if="scheduleDocSuccess" class="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
              {{ scheduleDocSuccess }}
            </div>
            <div v-if="scheduleDocError" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {{ scheduleDocError }}
            </div>
            
            <h2 class="text-lg font-semibold mb-2">Materias del Periodo {{ periodoActualId }}:</h2>
            <!-- Vista móvil -->
            <div class="grid grid-cols-1 gap-4 sm:hidden">
              <!-- Para vista específica de día -->
              <template v-if="filtroSemana !== 'todos'">
                <div 
                  v-for="(materia, index) in materiasDiaFiltradas"
                  :key="index"
                  class="bg-white shadow overflow-hidden rounded-md border-l-4"
                  :class="[
                    esPeriodoPasado(materia.date_end) ? 'border-gray-300 bg-gray-100 opacity-50' : 'border-blue-500'
                  ]"
                >
                  <div class="p-4">
                    <h3 class="text-lg font-bold mb-2" 
                        :class="esPeriodoPasado(materia.date_end) ? 'text-gray-400' : ''">
                      {{ materia.subject }}
                    </h3>
                    
                    <div class="grid grid-cols-2 gap-y-2">
                      <div class="text-sm font-medium" 
                           :class="esPeriodoPasado(materia.date_end) ? 'text-gray-400' : 'text-gray-500'">
                        Profesor:
                      </div>
                      <div class="text-sm" 
                           :class="esPeriodoPasado(materia.date_end) ? 'text-gray-400' : ''">
                        {{ materia.teacher }}
                      </div>
                      
                      <div class="text-sm font-medium" 
                           :class="esPeriodoPasado(materia.date_end) ? 'text-gray-400' : 'text-gray-500'">
                        Horario:
                      </div>
                      <div class="text-sm" 
                           :class="esPeriodoPasado(materia.date_end) ? 'text-gray-400' : ''">
                        {{ materia.duration }}
                      </div>
                      
                      <div class="text-sm font-medium" 
                           :class="esPeriodoPasado(materia.date_end) ? 'text-gray-400' : 'text-gray-500'">
                        Duración:
                      </div>
                      <div class="text-sm" 
                           :class="esPeriodoPasado(materia.date_end) ? 'text-gray-400' : ''">
                        {{ formatearFecha(materia.date_start) }} al {{ formatearFecha(materia.date_end) }}
                      </div>
                    </div>

                    <!-- Botón de acción para móvil -->
                    <div v-if="materia.documents > 0" class="flex justify-end mt-4 pt-3 border-t border-gray-200">
                      <button @click="openDocumentsModal(materia)"
                        class="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        Ver documentos académicos
                      </button>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Para vista de "Todos" -->
              <template v-else>
                <div 
                  v-for="(grupoMaterias, dia) in materiasPorDia"
                  :key="dia"
                  class="bg-gray-100 overflow-hidden rounded-md"
                >
                  <div class="p-4 bg-gray-100 font-semibold">{{ obtenerNombreDia(dia) }}</div>
                  
                  <div 
                    v-for="(materiaGrupo, index) in grupoMaterias" 
                    :key="index" 
                    class="border-gray-200 mb-2"
                    :class="esPeriodoPasado(materiaGrupo.date_end) ? 'bg-gray-100 opacity-50' : 'bg-white shadow overflow-hidden rounded-md border-l-4 border-blue-500'"
                  >
                    <div class="p-4">
                      <h3 class="text-lg font-bold mb-2" 
                          :class="esPeriodoPasado(materiaGrupo.date_end) ? 'text-gray-400' : ''">
                        {{ materiaGrupo.subject }}
                      </h3>
                      
                      <div class="grid grid-cols-2 gap-y-2">
                        <div class="text-sm font-medium" 
                             :class="esPeriodoPasado(materiaGrupo.date_end) ? 'text-gray-400' : 'text-gray-500'">
                          Profesor:
                        </div>
                        <div class="text-sm" 
                             :class="esPeriodoPasado(materiaGrupo.date_end) ? 'text-gray-400' : ''">
                          {{ materiaGrupo.teacher }}
                        </div>
                        
                        <div class="text-sm font-medium" 
                             :class="esPeriodoPasado(materiaGrupo.date_end) ? 'text-gray-400' : 'text-gray-500'">
                          Horario:
                        </div>
                        <div class="text-sm" 
                             :class="esPeriodoPasado(materiaGrupo.date_end) ? 'text-gray-400' : ''">
                          {{ materiaGrupo.duration }}
                        </div>
                        
                        <div class="text-sm font-medium" 
                             :class="esPeriodoPasado(materiaGrupo.date_end) ? 'text-gray-400' : 'text-gray-500'">
                          Duración:
                        </div>
                        <div class="text-sm" 
                             :class="esPeriodoPasado(materiaGrupo.date_end) ? 'text-gray-400' : ''">
                          {{ formatearFecha(materiaGrupo.date_start) }} al {{ formatearFecha(materiaGrupo.date_end) }}
                        </div>
                      </div>

                      <!-- Botón de acción para móvil -->
                      <div v-if="materiaGrupo.documents > 0" class="flex justify-end mt-4 pt-3 border-t border-gray-200">
                        <button @click="openDocumentsModal(materiaGrupo)"
                          class="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                          Ver documentos académicos
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Vista de escritorio -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="py-2 pl-4 pr-1 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Materia</th>
                    <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Profesor</th>
                    <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Horario</th>
                    <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Duración</th>
                    <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Opciones</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white text-sm font-medium text-gray-900">
                  <!-- Vista específica de día -->
                  <template v-if="filtroSemana !== 'todos'">
                    <tr 
                      v-for="(materia, index) in materiasDiaFiltradas"
                      :key="index"
                      :class="[
                        'hover:bg-gray-100',
                        esPeriodoPasado(materia.date_end) ? 'opacity-50 bg-gray-100' : ''
                      ]"
                    >
                      <td class="py-2 pl-4 pr-1 text-sm font-medium text-gray-900">
                        {{ materia.subject }}
                      </td>
                      <td class="px-3 py-4 md:whitespace-normal lg:whitespace-nowrap">
                        <div class="md:block lg:inline">
                          {{ materia.teacher }}
                        </div>
                      </td>
                      <td class="whitespace-nowrap px-3 py-4">
                        {{ materia.duration }}
                      </td>
                      <td class="px-3 py-4 md:whitespace-normal lg:whitespace-nowrap">
                        <div class="md:block lg:inline">
                          <span>{{ formatearFecha(materia.date_start) }}</span> al
                          <span>{{ formatearFecha(materia.date_end) }}</span>
                        </div>
                      </td>
                      <td class="px-3 py-4 text-center">
                        <div v-if="materia.documents > 0" class="buttons-container">
                          <div class="tooltip-wrapper">
                            <button @click="openDocumentsModal(materia)"
                              class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                              </svg>
                            </button>
                            <div class="tooltip tooltip-details">
                              <span class="tooltip-text">Ver documentos académicos</span>
                              <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                                <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <span v-else class="text-gray-400 text-xs">Sin documentos</span>
                      </td>
                    </tr>
                  </template>

                  <!-- Vista de "Todos" -->
                  <template v-else>
                    <template v-for="(grupoMaterias, dia) in materiasPorDia" :key="dia">
                      <tr class="text-center font-semibold uppercase">
                        <td colspan="6" class="py-2 pl-4 text-sm">
                          {{ obtenerNombreDia(dia) }}
                        </td>
                      </tr>
                      <tr 
                        v-for="(materia, index) in grupoMaterias"
                        :key="index"
                        :class="[
                          'hover:bg-gray-100',
                          esPeriodoPasado(materia.date_end) ? 'opacity-50 bg-gray-100' : ''
                        ]"
                      >
                        <td class="py-2 pl-4 pr-1 text-sm font-medium text-gray-900">
                          {{ materia.subject }}
                        </td>
                        <td class="px-3 py-4 md:whitespace-normal lg:whitespace-nowrap">
                          <div class="md:block lg:inline">
                            {{ materia.teacher }}
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-4">
                          {{ materia.duration }}
                        </td>
                        <td class="px-3 py-4 md:whitespace-normal lg:whitespace-nowrap">
                          <div class="md:block lg:inline">
                            <span>{{ formatearFecha(materia.date_start) }}</span> al
                            <span>{{ formatearFecha(materia.date_end) }}</span>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-center">
                          <div v-if="materia.documents > 0" class="buttons-container">
                            <div class="tooltip-wrapper">
                              <button @click="openDocumentsModal(materia)"
                                class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                              </button>
                              <div class="tooltip tooltip-details">
                                <span class="tooltip-text">Ver documentos académicos</span>
                                <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                                  <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <span v-else class="text-gray-400 text-xs">Sin documentos</span>
                        </td>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </table>
            </div>

            <!-- Mensaje de no hay materias -->
            <div v-if="(filtroSemana === 'todos' && Object.keys(materiasPorDia).length === 0) || 
                       (filtroSemana !== 'todos' && materiasDiaFiltradas.length === 0)" 
                 class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 text-center">
              No hay materias disponibles para mostrar.
            </div>
          </div>
          
          <div v-else class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            No hay materias disponibles para mostrar.
          </div>
        </div>
      </main>
    </div>

    <!-- Modal de Documentos Académicos -->
    <DocumentsModal
      :isVisible="showDocumentsModal"
      :selectedSubject="selectedSubjectForDocuments"
      :studentId="studentId"
      @close="closeDocumentsModal"
    />

    <!-- Modal de Visualización de Horario PDF -->
    <HorarioDocumentModal
      :isVisible="showScheduleDocModal"
      :pdfBlobUrl="schedulePdfBlobUrl"
      :documentName="scheduleDocName"
      @close="closeScheduleDocModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import DocumentsModal from '@/components/DocumentsModal.vue';
import HorarioDocumentModal from '@/components/HorarioDocumentModal.vue';
import { getSchedule, getPeriods, getScheduleDocument } from "@/services/api";

export default {
  components: {
    SidebarMenu,
    DocumentsModal,
    HorarioDocumentModal
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      data: [],
      loading: true,
      error: null,
      studentId: null,
      filtroSemana: 'todos', // Filtro por defecto: mostrar todos los días
      diasConClases: [], // Días de la semana que tienen clases
      diasSemana: [
        { numero: '1', nombre: 'Lunes' },
        { numero: '2', nombre: 'Martes' },
        { numero: '3', nombre: 'Miércoles' },
        { numero: '4', nombre: 'Jueves' },
        { numero: '5', nombre: 'Viernes' },
        { numero: '6', nombre: 'Sábado' },
        { numero: '7', nombre: 'Domingo' }
      ],
      periodoActual: null,
      periodoActualId: "", // Para almacenar el ID del periodo más reciente
      periodos: [], // Para almacenar la lista de periodos
      dataAgrupada: {},

      // Modal de documentos
      showDocumentsModal: false,
      selectedSubjectForDocuments: null,

      // Descarga/Visualización de horario PDF
      loadingScheduleDoc: false,
      scheduleDocSuccess: '',
      scheduleDocError: '',
      showScheduleDocModal: false,
      schedulePdfBlobUrl: null,
      scheduleDocName: ''
    };
  },
  computed: {
    // Materias filtradas por día
    materiasDiaFiltradas() {
      if (!this.data || this.data.length === 0) return [];
      
      // Filtramos solo por día (sin filtro de período)
      return this.data.filter(materia => 
        materia.day_number === this.filtroSemana
      );
    },
    // Materias agrupadas por día
    materiasPorDia() {
      if (this.filtroSemana !== 'todos') return {};

      const agrupadas = {};
      this.data.forEach(materia => {
        if (!agrupadas[materia.day_number]) {
          agrupadas[materia.day_number] = [];
        }
        agrupadas[materia.day_number].push(materia);
      });

      // Ordenar cada día por duración
      Object.keys(agrupadas).forEach(dia => {
        agrupadas[dia].sort((a, b) => {
          // Primero ordenar por hora de inicio
          const horaA = a.duration.split('-')[0].trim();
          const horaB = b.duration.split('-')[0].trim();
          return horaA.localeCompare(horaB);
        });
      });

      return agrupadas;
    },
  },
  async created() {
    // Inicializar la fecha actual
    this.fechaActual = new Date();
    
    // Obtener el usuario e inicializar datos
    await this.getUserFromBackend();
  },
  beforeUnmount() {
    //Liberar blob URL al destruir el componente
    this.revokeScheduleBlobUrl();
  },
  methods: {
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];
          
          // Cargar datos con el ID extraído
          await this.loadPeriods();
          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    async loadPeriods() {
      try {
        const response = await getPeriods({ idstudent: this.studentId });
        this.periodos = response.data;
        
        // Obtener el ID del periodo más reciente (primer elemento)
        if (this.periodos && this.periodos.length > 0) {
          this.periodoActualId = this.periodos[0].idPeriod;
        }
      } catch (error) {
        console.error("Error al obtener los periodos:", error);
      }
    },
    async loadData() {
      try {
        const response = await getSchedule({ idstudent: this.studentId });
        
        // Ordenar los datos por fecha de inicio (más reciente primero)
        this.data = this.ordenarPorFechaReciente(response.data);
        
        // Identificar periodos
        this.identificarPeriodoActual();
        
        // Determinar qué días de la semana tienen clases
        this.identificarDiasConClases();
        
        // Agrupar materias por nombre
        this.agruparMaterias();
        
        this.loading = false;
      } catch (error) {
        console.error("Error al obtener los datos:", error);
        this.error = "Error al cargar los datos. Por favor, intente nuevamente.";
        this.loading = false;
      }
    },
    // Obtener el PDF del horario desde la API
    async fetchSchedulePdf() {
      this.loadingScheduleDoc = true;
      this.scheduleDocError = '';
      this.scheduleDocSuccess = '';

      try {
        const response = await getScheduleDocument({ idstudent: this.studentId });

        // Crear blob URL a partir del response (application/pdf)
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        this.scheduleDocName = `doc_${this.studentId}_horario.pdf`;

        return blobUrl;
      } catch (error) {
        console.error("Error al obtener el documento de horario:", error);
        this.scheduleDocError = "Error al obtener el documento de horario. Por favor, intente nuevamente.";
        return null;
      } finally {
        this.loadingScheduleDoc = false;
      }
    },
    // Visualizar horario PDF en modal
    async viewScheduleDocument() {
      // Liberar blob URL anterior si existe
      this.revokeScheduleBlobUrl();

      const blobUrl = await this.fetchSchedulePdf();
      if (blobUrl) {
        this.schedulePdfBlobUrl = blobUrl;
        this.showScheduleDocModal = true;
      }
    },
    // Descargar horario PDF directamente
    async downloadScheduleDocument() {
      const blobUrl = await this.fetchSchedulePdf();
      if (blobUrl) {
        try {
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `doc_${this.studentId}_horario.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.scheduleDocSuccess = 'Descarga del horario iniciada.';
          setTimeout(() => {
            this.scheduleDocSuccess = '';
          }, 3000);

          // Liberar el blob URL después de la descarga
          URL.revokeObjectURL(blobUrl);
        } catch (error) {
          console.error('Error iniciando descarga del horario:', error);
          this.scheduleDocError = 'Error al iniciar la descarga del horario.';
        }
      }
    },
    // Cerrar modal de visualización de horario PDF
    closeScheduleDocModal() {
      this.showScheduleDocModal = false;
      this.revokeScheduleBlobUrl();
    },
    // Liberar blob URL del horario
    revokeScheduleBlobUrl() {
      if (this.schedulePdfBlobUrl) {
        URL.revokeObjectURL(this.schedulePdfBlobUrl);
        this.schedulePdfBlobUrl = null;
      }
    },
    // Ordenar datos por fecha más reciente primero
    ordenarPorFechaReciente(datos) {
      return [...datos].sort((a, b) => {
        return new Date(b.date_start) - new Date(a.date_start);
      });
    },
    // Identificar el período académico actual
    identificarPeriodoActual() {
      const fechaActual = new Date();
      
      // Encontrar todos los períodos disponibles
      const periodos = new Set();
      this.data.forEach(item => {
        const key = `${item.date_start}|${item.date_end}`;
        periodos.add(key);
      });
      
      // Verificar cuál es el período actual (donde la fecha actual está entre start y end)
      for (const periodo of periodos) {
        const [start, end] = periodo.split('|');
        const startDate = new Date(start);
        const endDate = new Date(end);
        
        if (fechaActual >= startDate && fechaActual <= endDate) {
          this.periodoActual = { start, end };
          break;
        }
      }
      
      // Si no se encontró un período actual, usar el más reciente
      if (!this.periodoActual && periodos.size > 0) {
        const periodosArray = Array.from(periodos);
        // Ordenar períodos por fecha de inicio más reciente
        periodosArray.sort((a, b) => {
          const startDateA = new Date(a.split('|')[0]);
          const startDateB = new Date(b.split('|')[0]);
          return startDateB - startDateA;
        });
        
        const [start, end] = periodosArray[0].split('|');
        this.periodoActual = { start, end };
      }
    },
    // Comprobar si un período ya ha pasado
    esPeriodoPasado(fechaFin) {
      const fechaActual = new Date();
      const fin = new Date(fechaFin);
      return fin < fechaActual;
    },
    // Identificar los días de la semana que tienen clases
    identificarDiasConClases() {
      const diasSet = new Set();
      this.data.forEach(materia => {
        diasSet.add(materia.day_number);
      });
      this.diasConClases = Array.from(diasSet);
    },
    // Agrupar materias por nombre para la vista "Todos"
    agruparMaterias() {
      const materias = {};
      
      this.data.forEach(item => {
        // Si la materia no existe en el objeto, crearla
        if (!materias[item.subject]) {
          materias[item.subject] = {
            profesor: item.teacher,
            fechaInicio: item.date_start,
            fechaFin: item.date_end,
            horarios: []
          };
        }
        
        // Verificar si el horario ya existe
        const horarioExiste = materias[item.subject].horarios.some(
          h => h.dia === item.day_name && h.hora === item.duration
        );
        
        // Solo agregar el horario si no existe
        if (!horarioExiste) {
          materias[item.subject].horarios.push({
            dia: item.day_name,
            hora: item.duration
          });
        }
      });
      
      this.dataAgrupada = materias;
    },
    // Filtrar materias por día de la semana
    filtrarPorDia(diaNumero) {
      this.filtroSemana = diaNumero;
    },
    // Formatear fecha de ISO a formato legible
    formatearFecha(fechaStr) {
      const fecha = new Date(fechaStr);
      return fecha.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    // Obtener nombre de día a partir de su número
    obtenerNombreDia(diaNumero) {
      const dia = this.diasSemana.find(d => d.numero === diaNumero);
      return dia ? dia.nombre : diaNumero;
    },
    // Modal de documentos
    openDocumentsModal(subject) {
      this.selectedSubjectForDocuments = subject;
      this.showDocumentsModal = true;
    },
    closeDocumentsModal() {
      this.showDocumentsModal = false;
      this.selectedSubjectForDocuments = null;
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');

        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));

        // Realizar la petición de logout
        await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>